import { getSourceIconByCode } from "helpers/Sources/getSourceIcon";

// Intentionally free of `SourceCreation.constants` imports: that module pulls
// in every `source_types/*` component and these helpers are reused elsewhere.

export const appendDomainHttps = (value) => {
    value = value.replace("http://", "");
    value = value.replace("https://", "");
    value = "https://" + value;
    value = value.trim();
    return value;
};

/** Drops the protocol and a leading `www.` from a URL-ish string. */
export const stripProtocol = (url) =>
    url.replace(/^https?:\/\//, "").replace("www.", "");

export const createSubDomain = (domain) => {
    try {
        const url = new URL(domain);
        return url.hostname.replace("www.", "");
    } catch (error) {
        return "";
    }
};

const cannotBeBlank = (language) =>
    language?.tracker_wizard?.cannot_be_blank ?? "Cannot be blank!";

/** Validates `source_type` and `domain` only (generic-domain mode). */
export const findRequiredFieldErrors = ({ form, language }) => {
    const { source_type, domain } = form;
    const errors = {};
    if (!source_type) errors.source_type = cannotBeBlank(language);
    if (!domain) errors.domain = cannotBeBlank(language);
    return errors;
};

/** Validates all custom-host fields (`source_type`, `domain`, `host`, `subdomain`). */
export const findFormErrors = ({ form, language, subdomain }) => {
    const { source_type, domain, host } = form;
    const errors = {};
    if (!source_type) errors.source_type = cannotBeBlank(language);
    if (!domain) errors.domain = cannotBeBlank(language);
    if (!host) errors.host = cannotBeBlank(language);
    if (!subdomain) errors.subdomain = cannotBeBlank(language);
    return errors;
};

export const getSourceTypeOptions = (sourceTypes) =>
    sourceTypes?.map((item) => ({
        value: item?.code,
        label: item?.name,
        image: getSourceIconByCode(item?.code),
    }));

export const getUsageOptions = (usageTypes) =>
    usageTypes?.map((item) => ({
        value: item?.code,
        label: item?.name,
        id: item?.id,
    })) ?? [];

/**
 * Converts a flat list of `a/b/c.csv` paths into `react-checkbox-tree` nodes.
 */
export const buildFileTree = (paths) => {
    const fileSystem = [];
    for (const path of paths) {
        const segments = path.split("/");
        let currentLevel = fileSystem;

        for (let i = 0; i < segments.length; i++) {
            const fileName = segments[i];
            const isLastItem = i === segments.length - 1;

            const existingNode = currentLevel.find(
                (node) => node.label === fileName,
            );

            if (!existingNode) {
                const newNode = {
                    value: isLastItem
                        ? path
                        : segments.slice(0, i + 1).join("/"),
                    label: fileName,
                };
                const isFolder = fileName.split(".").length === 1;

                if (isFolder || !isLastItem) {
                    newNode.children = [];
                    currentLevel.push(newNode);
                    currentLevel = newNode.children;
                } else {
                    currentLevel.push(newNode);
                }
            } else {
                if (isLastItem && !existingNode.children) {
                    existingNode.value = path;
                }
                currentLevel = existingNode.children || [];
            }
        }
    }
    return fileSystem;
};

export const getFolderPath = (path) =>
    path.replace(path.replace(/^.*[\\/]/, ""), "");

export const areInSameFolder = (paths) =>
    paths.every((path) => getFolderPath(path) === getFolderPath(paths[0]));

/**
 * Picks the lead mapping fields a CRM exposes. For each `mappingKey -> apiValue`
 * in `keys`, the mapping becomes `apiValue` when present in `fields`, otherwise `"none"`.
 */
export const buildLeadMappings = (fields, keys) => {
    const hasField = (apiValue) =>
        !!fields?.some((field) => field?.api_value === apiValue);
    return Object.entries(keys).reduce((mappings, [mappingKey, apiValue]) => {
        mappings[mappingKey] = hasField(apiValue) ? apiValue : "none";
        return mappings;
    }, {});
};

export const toLeadMappingPayload = (leadMappings) => ({
    lead_id: leadMappings?.leadgen_id || "none",
    lead_status: leadMappings?.leadStatus || "none",
    lead_date: leadMappings?.date || "none",
});

export const toSpreadsheetPageOptions = (pages) =>
    pages?.map((page) => ({
        id: page?.index,
        name: page?.name,
        label: page?.name,
        value: page?.name,
    })) ?? [];
